import { useEffect, useMemo, useRef, useState } from 'react'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useMoneda } from '../context/MonedaContext'
import TicketImprimible from '../components/TicketImprimible'

const METODOS_PAGO = [
  { value: 'efectivo',      label: '💵 Efectivo' },
  { value: 'tarjeta',       label: '💳 Tarjeta' },
  { value: 'transferencia', label: '🏦 Transferencia' },
]

// ── Utilidades RUT ─────────────────────────────────────────────────────────────

const validarRut = (rut) => {
  if (!rut) return true
  const limpio = rut.replace(/[^0-9kK]/g, '')
  if (limpio.length < 2) return false
  const cuerpo = limpio.slice(0, -1)
  const dv     = limpio.slice(-1).toUpperCase()
  let suma = 0, multiplo = 2
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplo
    multiplo = multiplo === 7 ? 2 : multiplo + 1
  }
  const dvEsperado = 11 - (suma % 11)
  const dvCalc = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : String(dvEsperado)
  return dv === dvCalc
}

const formatearRut = (rut) => {
  const limpio = rut.replace(/[^0-9kK]/g, '')
  if (limpio.length < 2) return rut
  const cuerpo = limpio.slice(0, -1)
  const dv     = limpio.slice(-1).toUpperCase()
  return cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + dv
}

// ── Modal: Nuevo cliente rápido ────────────────────────────────────────────────
function ModalNuevoCliente({ clientes, onGuardado, onCerrar }) {
  const [form, setForm] = useState({ razon_social: '', rut: '', email: '', telefono: '' })
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const primerCampoRef = useRef()

  useEffect(() => { primerCampoRef.current?.focus() }, [])

  const handleChange = (campo) => (e) => {
    setForm(prev => ({ ...prev, [campo]: e.target.value }))
    setError('')
  }

  const handleGuardar = async () => {
    const nombre = form.razon_social.trim()
    if (!nombre) { setError('El nombre es obligatorio.'); return }

    if (form.rut && !validarRut(form.rut)) {
      setError('RUT inválido. Verifica el dígito verificador.')
      return
    }

    setGuardando(true)
    const nuevoContacto = {
      id: String(Date.now()),
      tipo: 'cliente',
      razon_social: nombre,
      rut: form.rut.trim() ? formatearRut(form.rut.trim()) : null,
      email: form.email.trim() || null,
      telefono: form.telefono.trim() || null,
      activo: true,
    }

    try {
      const data = await apiFetch('/contactos', {
        method: 'POST',
        body: JSON.stringify(nuevoContacto),
      })
      onGuardado(data || nuevoContacto)
    } catch (e) {
      console.warn('Guardando cliente localmente:', e)
      onGuardado(nuevoContacto)
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onCerrar} style={{ zIndex: 1100 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="modal-header">
          <h2 className="modal-title">👤 Nuevo Cliente</h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">
              Nombre / Razón Social <span style={{ color: 'var(--danger, #ef4444)' }}>*</span>
            </label>
            <input
              ref={primerCampoRef}
              className="form-input"
              value={form.razon_social}
              onChange={handleChange('razon_social')}
              placeholder="Ej: Juan Pérez"
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">RUT <span style={{ color: 'var(--text-light)' }}>(opcional)</span></label>
            <input
              className="form-input"
              value={form.rut}
              onChange={handleChange('rut')}
              onBlur={e => {
                if (e.target.value) {
                  const formateado = formatearRut(e.target.value)
                  setForm(prev => ({ ...prev, rut: formateado }))
                  if (!validarRut(e.target.value))
                    setError('RUT inválido. Verifica el dígito verificador.')
                }
              }}
              placeholder="Ej: 12.345.678-9"
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Email <span style={{ color: 'var(--text-light)' }}>(opcional)</span></label>
            <input
              className="form-input"
              type="email"
              value={form.email}
              onChange={handleChange('email')}
              placeholder="cliente@ejemplo.cl"
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Teléfono <span style={{ color: 'var(--text-light)' }}>(opcional)</span></label>
            <input
              className="form-input"
              value={form.telefono}
              onChange={handleChange('telefono')}
              placeholder="+56 9 1234 5678"
            />
          </div>

          {error && (
            <p style={{ color: 'var(--danger, #ef4444)', fontSize: '0.875rem', margin: 0 }}>
              {error}
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={guardando}>
            {guardando ? '⏳ Guardando...' : '✅ Guardar Cliente'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function Ventas() {
  const { perfil, sucursalActiva } = useAuth()
  const { formatearMoneda } = useMoneda()

  // Productos y carrito
  const [productos,      setProductos]      = useState([])
  const [carrito,        setCarrito]        = useState([])
  const [busqueda,       setBusqueda]       = useState('')
  const [loading,        setLoading]        = useState(true)

  // Venta y modales
  const [ticket,         setTicket]         = useState(null)
  const [modalConfirmar, setModalConfirmar] = useState(false)
  const [procesando,     setProcesando]     = useState(false)
  const [metodoPago,     setMetodoPago]     = useState('efectivo')

  // Clientes
  const [clientes,          setClientes]          = useState([])
  const [clienteId,         setClienteId]         = useState('')
  const [busquedaCliente,   setBusquedaCliente]   = useState('')
  const [modalNuevoCliente, setModalNuevoCliente] = useState(false)

  // IVA
  const [ivaRate, setIvaRate] = useState(0.19)

  const busquedaRef = useRef()
  const sucursalId = sucursalActiva?.id || 1

  useEffect(() => {
    setLoading(true)
    Promise.all([cargarProductos(), cargarClientes()])
  }, [sucursalId])

  const cargarProductos = async () => {
    try {
      const data = await apiFetch(`/inventario/productos?sucursal_id=${sucursalId}`)
      if (Array.isArray(data)) {
        setProductos(data)
      } else {
        setProductos([])
      }
    } catch (e) {
      console.warn('No se obtuvieron productos del servidor:', e)
      setProductos([])
    } finally {
      setLoading(false)
    }
  }

  const cargarClientes = async () => {
    try {
      const data = await apiFetch('/contactos?tipo=cliente')
      if (Array.isArray(data)) {
        setClientes(data)
      } else {
        setClientes([])
      }
    } catch (e) {
      console.warn('No se obtuvieron clientes del servidor:', e)
      setClientes([])
    }
  }

  // ── Carrito ──────────────────────────────────────────────────────────────────
  const agregarAlCarrito = (producto) => {
    const idProducto = producto?.producto_id || producto?.id
    if (!idProducto) return

    const existe = carrito.find(it => it.producto_id === idProducto)

    if (existe) {
      if (existe.cantidad >= producto.stock_actual) {
        alert('No hay suficiente stock en esta sucursal')
        return
      }
      setCarrito(prev =>
        prev.map(it =>
          it.producto_id === idProducto
            ? { ...it, cantidad: it.cantidad + 1 }
            : it
        )
      )
    } else {
      setCarrito(prev => [
        ...prev,
        {
          producto_id:     idProducto,
          nombre:          producto.nombre,
          precio:          Number(producto.precio_venta),
          cantidad:        1,
          stock_disponible: Number(producto.stock_actual),
        },
      ])
    }
  }

  const modificarCantidad = (productoId, nuevaCantidad) => {
    if (nuevaCantidad <= 0) { eliminarDelCarrito(productoId); return }
    const item = carrito.find(i => i.producto_id === productoId)
    if (!item) return
    if (nuevaCantidad > item.stock_disponible) {
      alert('No hay suficiente stock en esta sucursal')
      return
    }
    setCarrito(prev =>
      prev.map(it =>
        it.producto_id === productoId ? { ...it, cantidad: nuevaCantidad } : it
      )
    )
  }

  const eliminarDelCarrito = (productoId) =>
    setCarrito(prev => prev.filter(it => it.producto_id !== productoId))

  const totalVenta = useMemo(
    () => carrito.reduce((t, it) => t + Number(it.precio) * Number(it.cantidad), 0),
    [carrito]
  )

  const handleBusquedaKeyDown = (e) => {
    if (e.key !== 'Enter') return
    if (productosFiltrados.length === 1) {
      agregarAlCarrito(productosFiltrados[0])
      setBusqueda('')
      busquedaRef.current?.focus()
    } else if (productosFiltrados.length === 0) {
      busquedaRef.current?.select()
    }
  }

  const productosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return productos
    return productos.filter(p =>
      p.nombre.toLowerCase().includes(q) ||
      (p.codigo || '').toLowerCase().includes(q)
    )
  }, [productos, busqueda])

  const clientesFiltrados = useMemo(() => {
    const q = busquedaCliente.trim().toLowerCase()
    if (!q) return clientes
    return clientes.filter(c =>
      (c.razon_social || c.nombre || '').toLowerCase().includes(q) ||
      (c.rut || '').toLowerCase().includes(q) ||
      (c.nombre_fantasia || '').toLowerCase().includes(q)
    )
  }, [clientes, busquedaCliente])

  const clienteSeleccionado = clientes.find(c => c.id === clienteId)

  const handleClienteGuardado = (nuevoCliente) => {
    setClientes(prev => [...prev, nuevoCliente].sort((a, b) =>
      (a.razon_social || a.nombre || '').localeCompare(b.razon_social || b.nombre || '')
    ))
    setClienteId(nuevoCliente.id)
    setBusquedaCliente(nuevoCliente.razon_social || nuevoCliente.nombre)
    setModalNuevoCliente(false)
  }

  const procesarVenta = async () => {
    if (procesando) return
    if (carrito.length === 0) { alert('El carrito está vacío.'); return }

    setProcesando(true)
    try {
      const items = carrito.map(it => ({
        producto_id: it.producto_id,
        cantidad: Number(it.cantidad),
      }))

      const payload = {
        sucursal_id: sucursalId,
        metodo_pago: metodoPago,
        cliente_id: clienteId || null,
        items,
      }

      let data = null
      try {
        data = await apiFetch('/ventas', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } catch (e) {
        console.warn('Procesando venta localmente:', e)
      }

      const folioSimulado = `VEN-${Math.floor(Math.random() * 9000 + 1000)}`

      setModalConfirmar(false)
      setTicket({
        venta: {
          id: data?.venta_id || String(Date.now()),
          folio: data?.folio || folioSimulado,
          fecha: new Date().toISOString(),
          metodo_pago: metodoPago,
          total: data?.total ?? totalVenta,
        },
        detalles: carrito.map(it => ({
          nombre: it.nombre,
          cantidad: it.cantidad,
          precio_unitario: it.precio,
          subtotal: Number(it.precio) * Number(it.cantidad),
        })),
      })

      setCarrito([])
      setMetodoPago('efectivo')
      setClienteId('')
      setBusquedaCliente('')
      await cargarProductos()
    } catch (e) {
      alert('Error al procesar la venta: ' + (e?.message || 'Error desconocido'))
    } finally {
      setProcesando(false)
    }
  }

  if (loading) {
    return (
      <div className="main-content">
        <div className="page-header"><h1 className="page-title">Nueva Venta</h1></div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Cargando catálogo...</div>
      </div>
    )
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Nueva Venta</h1>
        <p className="page-subtitle">Selecciona productos o escanea un código de barras — {sucursalActiva?.nombre || 'Casa Matriz'}</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>

        {/* ── Panel izquierdo: productos ── */}
        <div className="card">
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
            Productos Disponibles
          </h2>

          <div className="search-box" style={{ marginBottom: '1rem' }}>
            <input
              ref={busquedaRef}
              type="text"
              className="search-input"
              placeholder="Buscar por nombre o código — Enter para agregar"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              onKeyDown={handleBusquedaKeyDown}
              autoFocus
            />
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                      {busqueda ? 'No se encontraron productos' : 'No hay productos disponibles en el catálogo'}
                    </td>
                  </tr>
                ) : productosFiltrados.map(producto => (
                  <tr key={producto.stock_sucursal_id || producto.producto_id || producto.id}>
                    <td>
                      <div style={{ fontWeight: '500' }}>{producto.nombre}</div>
                      {producto.codigo && (
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                          {producto.codigo}
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: '600' }}>{formatearMoneda(producto.precio_venta)}</td>
                    <td>
                      <span className={producto.stock_actual <= (producto.stock_minimo || 5) ? 'badge badge-warning' : ''}>
                        {producto.stock_actual}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-primary"
                        onClick={() => agregarAlCarrito(producto)}
                        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      >
                        + Agregar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Panel derecho: carrito ── */}
        <div className="card">
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
            Carrito ({carrito.length})
          </h2>

          {carrito.length === 0 ? (
            <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: '2rem' }}>
              El carrito está vacío
            </p>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                {carrito.map(item => (
                  <div
                    key={item.producto_id}
                    style={{ padding: '1rem', borderBottom: '1px solid var(--border)', marginBottom: '0.5rem' }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>{item.nombre}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem' }}>
                      {formatearMoneda(item.precio)} c/u
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        className="btn btn-outline"
                        onClick={() => modificarCantidad(item.producto_id, item.cantidad - 1)}
                        style={{ padding: '0.25rem 0.5rem' }}
                      >−</button>

                      <input
                        type="number"
                        value={item.cantidad}
                        onChange={e => modificarCantidad(item.producto_id, parseInt(e.target.value) || 0)}
                        style={{
                          width: '60px', textAlign: 'center', padding: '0.25rem',
                          border: '1px solid var(--border)', borderRadius: '0.25rem'
                        }}
                        min="1"
                        max={item.stock_disponible}
                      />

                      <button
                        className="btn btn-outline"
                        onClick={() => modificarCantidad(item.producto_id, item.cantidad + 1)}
                        style={{ padding: '0.25rem 0.5rem' }}
                      >+</button>

                      <button
                        className="btn btn-danger"
                        onClick={() => eliminarDelCarrito(item.producto_id)}
                        style={{ padding: '0.25rem 0.5rem', marginLeft: 'auto' }}
                      >✕</button>
                    </div>

                    <div style={{ marginTop: '0.5rem', fontWeight: '600' }}>
                      Subtotal: {formatearMoneda(item.precio * item.cantidad)}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '2px solid var(--border)', paddingTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: '700' }}>Total:</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary)' }}>
                    {formatearMoneda(totalVenta)}
                  </span>
                </div>

                <button
                  className="btn btn-secondary"
                  onClick={() => setModalConfirmar(true)}
                  style={{ width: '100%', padding: '0.875rem' }}
                >
                  Revisar y Confirmar Venta
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modal confirmar venta ── */}
      {modalConfirmar && (
        <div className="modal-overlay" onClick={() => !procesando && setModalConfirmar(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Confirmar Venta</h2>
            </div>

            <p style={{ marginBottom: '1rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>
              Revisa los productos antes de confirmar.
            </p>

            <div className="table-container" style={{ marginBottom: '1rem' }}>
              <table>
                <thead>
                  <tr><th>Producto</th><th>Cant.</th><th>Precio</th><th>Subtotal</th></tr>
                </thead>
                <tbody>
                  {carrito.map(item => (
                    <tr key={item.producto_id}>
                      <td style={{ fontWeight: '500' }}>{item.nombre}</td>
                      <td>{item.cantidad}</td>
                      <td>{formatearMoneda(item.precio)}</td>
                      <td style={{ fontWeight: '600' }}>{formatearMoneda(item.precio * item.cantidad)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)' }}>
                    <td colSpan="3" style={{ fontWeight: '700', textAlign: 'right', padding: '0.75rem' }}>
                      Neto:
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {formatearMoneda(Math.round(totalVenta / (1 + ivaRate)))}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'right', padding: '0.25rem 0.75rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>
                      IVA ({Math.round(ivaRate * 100)}%):
                    </td>
                    <td style={{ padding: '0.25rem 0.75rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>
                      {formatearMoneda(totalVenta - Math.round(totalVenta / (1 + ivaRate)))}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" style={{ fontWeight: '700', textAlign: 'right', padding: '0.75rem' }}>Total:</td>
                    <td style={{ fontWeight: '700', fontSize: '1.125rem', color: 'var(--primary)', padding: '0.75rem' }}>
                      {formatearMoneda(totalVenta)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Cliente <span style={{ color: 'var(--text-light)', fontWeight: '400' }}>(opcional)</span>
                </label>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setModalNuevoCliente(true)}
                  style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
                >
                  + Nuevo cliente
                </button>
              </div>

              <input
                className="form-input"
                placeholder="Buscar por nombre o RUT..."
                value={busquedaCliente}
                onChange={e => {
                  setBusquedaCliente(e.target.value)
                  if (!e.target.value) setClienteId('')
                }}
                style={{ marginBottom: '0.5rem' }}
              />

              {busquedaCliente && !clienteSeleccionado && (
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: '0.5rem',
                  maxHeight: '160px',
                  overflowY: 'auto',
                  background: 'white',
                }}>
                  {clientesFiltrados.length === 0 ? (
                    <div style={{ padding: '0.75rem', color: 'var(--text-light)', fontSize: '0.875rem', textAlign: 'center' }}>
                      No se encontraron clientes
                    </div>
                  ) : clientesFiltrados.map(c => (
                    <div
                      key={c.id}
                      onClick={() => {
                        setClienteId(c.id)
                        setBusquedaCliente((c.razon_social || c.nombre) + (c.rut ? ` — ${c.rut}` : ''))
                      }}
                      style={{
                        padding: '0.6rem 0.75rem',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--border)',
                        fontSize: '0.875rem',
                      }}
                    >
                      <span style={{ fontWeight: '500' }}>{c.razon_social || c.nombre}</span>
                      <span style={{ color: 'var(--text-light)', marginLeft: '0.5rem' }}>
                        {c.rut || <em>sin RUT</em>}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {clienteSeleccionado && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 0.75rem',
                  background: '#eff6ff',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                }}>
                  <span>✅ <strong>{clienteSeleccionado.razon_social || clienteSeleccionado.nombre}</strong>{clienteSeleccionado.rut ? ` — ${clienteSeleccionado.rut}` : ''}</span>
                  <button
                    type="button"
                    onClick={() => { setClienteId(''); setBusquedaCliente('') }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)' }}
                  >✕</button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Método de Pago</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {METODOS_PAGO.map(m => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMetodoPago(m.value)}
                    style={{
                      flex: 1, padding: '0.6rem',
                      border: `2px solid ${metodoPago === m.value ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: '0.5rem',
                      background: metodoPago === m.value ? '#eff6ff' : 'white',
                      cursor: 'pointer',
                      fontWeight: metodoPago === m.value ? '600' : '400',
                      fontSize: '0.8rem',
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModalConfirmar(false)} disabled={procesando}>
                ✕ Cancelar
              </button>
              <button className="btn btn-secondary" onClick={procesarVenta} disabled={procesando} style={{ minWidth: '160px' }}>
                {procesando ? '⏳ Procesando...' : '✅ Realizar Venta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalNuevoCliente && (
        <ModalNuevoCliente
          clientes={clientes}
          onGuardado={handleClienteGuardado}
          onCerrar={() => setModalNuevoCliente(false)}
        />
      )}

      {ticket && (
        <TicketImprimible
          venta={ticket.venta}
          detalles={ticket.detalles}
          empresa={perfil?.empresa_nombre || perfil?.empresa?.nombre || perfil?.empresas?.nombre || 'Puntum POS'}
          sucursal={sucursalActiva?.nombre || 'Casa Matriz'}
          vendedor={perfil?.nombre || 'Vendedor'}
          ivaRate={ivaRate}
          onCerrar={() => setTicket(null)}
        />
      )}
    </div>
  )
}